import React, { useState, useEffect, useRef } from "react";
import {
  MapPin,
  ShoppingCart,
  User,
  Search,
  ChevronDown,
  Globe,
  Store,
  Heart,
  Box,
  LogOut,
  LogIn,
} from "lucide-react";
import logo from "../../Assets/Logos/LogoKarigari.png";
import { useNavigate } from "react-router-dom";
import UserLoginModal from "../Login/UserLoginModal";
import toast from "react-hot-toast";
import { useCart } from "../../utils/CartContext";
import AddAddressModal from "../UserSideComponents/Address/AddAddressModal";
import AllAddressesModal from "../UserSideComponents/Address/AllAddressesModal";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext";
import { formatingPrice } from "../../utils/formatingPrice";

const TopNavbar = () => {
  const { t, i18n } = useTranslation();
  const { currency, setCurrency, convertPrice } = useCurrency();

  const [searchQuery, setSearchQuery] = useState("");
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [defaultAddress, setDefaultAddress] = useState(null);
  const [allAddresses, setAllAddresses] = useState([]);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { cartCount, fetchCartCount } = useCart();
  const [showAddAddressModal, setShowAddAddressModal] = useState(false);
  const [showAllAddressesModal, setShowAllAddressesModal] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [products, setProducts] = useState([]);
  const [addressesFetched, setAddressesFetched] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("pm-user-data");
    if (stored) {
      const user = JSON.parse(stored);
      setLoggedInUser(user);
      fetchCartCount(user._id);
      fetchUserAddresses(user._id);
    }
  }, [fetchCartCount]);

  // Listen for login events
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'pm-user-data') {
        const stored = localStorage.getItem("pm-user-data");
        if (stored) {
          const user = JSON.parse(stored);
          setLoggedInUser(user);
          fetchCartCount(user._id);
          fetchUserAddresses(user._id);
        } else {
          setLoggedInUser(null);
          setDefaultAddress(null);
          setAllAddresses([]);
          setAddressesFetched(false);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const fetchUserAddresses = async (userId) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/address/user/${userId}`
      );
      const data = await res.json();
      if (data.success && data.data.length > 0) {
        setAllAddresses(data.data);
        const defaultAddr = data.data.find((a) => a.isDefault);
        if (defaultAddr) {
          setDefaultAddress(defaultAddr);
        } else {
          setDefaultAddress(data.data[0]);
        }
      }
      setAddressesFetched(true);
    } catch (err) {
      console.error("Address fetch error:", err);
      setAddressesFetched(true);
    }
  };

  const handleLoginSuccess = (userData) => {
    setLoggedInUser(userData);
    localStorage.setItem("pm-user-data", JSON.stringify(userData));
    setShowLoginModal(false);
    toast.success(`${t("hello")}, ${userData.firstName || t("user")}!`);
    fetchCartCount(userData._id);
    fetchUserAddresses(userData._id);
  };

  const handleLogout = () => {
    localStorage.removeItem("pm-user-data");
    setLoggedInUser(null);
    setDefaultAddress(null);
    setAllAddresses([]);
    setAddressesFetched(false);
    toast.success(t("logoutSuccess"));
    fetchCartCount(null);
  };

  const handleAddressSelect = (address) => {
    setDefaultAddress(address);
    setShowAllAddressesModal(false);
  };

  const handleAddAddressClick = () => {
    if (allAddresses.length > 0) {
      setShowAllAddressesModal(true);
    } else {
      setShowAddAddressModal(true);
    }
  };

  const handleAddressAdded = (newAddress) => {
    setDefaultAddress(newAddress);
    setShowAddAddressModal(false);
    setAllAddresses([...allAddresses, newAddress]);
  };

  const handleCartClick = () => {
    navigate("/cart");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/products/get-all-products`
        );
        const data = await res.json();
        setAllProducts(data.products || []);
        setProducts(data.products || []);
      } catch (err) {
        console.error("Error fetching all products:", err);
      }
    };
    fetchAll();
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (value.trim() === "") {
      setShowResults(false);
      setSearchResults([]);
      return;
    }

    const results = filterProducts(value, products);
    setSearchResults(results);
    setShowResults(true);
  };

  useEffect(() => {
    if (searchQuery.trim().length > 0) {
      setShowResults(true);
    }
  }, [searchQuery]);

  const getProductName = (product) => {
    const name = product?.name;
    if (!name) return "";
    if (typeof name === "object") {
      return (
        name[i18n.language] ||
        name.en ||
        name.hi ||
        name.es ||
        name.zh ||
        ""
      );
    }
    return name || product.title || product.headerTitle || product.productName || "";
  };

  const filterProducts = (query, products) => {
    if (!query.trim()) return [];
    const lower = query.toLowerCase();
    return products.filter((p) => {
      const name = getProductName(p);
      if (!name || typeof name !== "string") return false;
      return name.toLowerCase().includes(lower);
    });
  };

  const handleSearchEnter = (e) => {
    if (e.key === "Enter") triggerSearch();
  };

  const triggerSearch = () => {
    if (!searchQuery.trim()) return;
    navigate(`/allproduct?search=${encodeURIComponent(searchQuery)}`);
    setShowResults(false);
  };

  return (
    <>
      {showLoginModal && (
        <UserLoginModal
          onClose={() => setShowLoginModal(false)}
          onLoginSuccess={handleLoginSuccess}
        />
      )}

      {showAddAddressModal && (
        <AddAddressModal
          isOpen={showAddAddressModal}
          onClose={() => setShowAddAddressModal(false)}
          onAddressAdded={handleAddressAdded}
        />
      )}

      {showAllAddressesModal && (
        <AllAddressesModal
          isOpen={showAllAddressesModal}
          onClose={() => setShowAllAddressesModal(false)}
          addresses={allAddresses}
          onAddressSelect={handleAddressSelect}
          selectedAddressId={defaultAddress?._id}
          onAddAddressClick={() => setShowAddAddressModal(true)}
        />
      )}

      {/* NAVBAR */}
      <nav className="bg-[#242a30] text-white sticky top-0 z-[999]">
        {/* DESKTOP VIEW */}
        <div className="hidden md:flex items-center justify-between px-4 md:px-10 py-2 gap-4">
          {/* LOGO */}
          <div
            onClick={() => navigate("/")}
            className="flex items-center gap-2 cursor-pointer"
          >
            <img src={logo} alt="Logo" className="h-12 w-auto" />
          </div>

          {loggedInUser && (
            <div
              className="flex items-center gap-2 cursor-pointer hover:bg-[#232F3E] p-2 rounded-md"
              onClick={handleAddAddressClick}
            >
              <MapPin className="w-5 h-5 text-white" />
              <div className="text-xs flex flex-col">
                <span className="text-gray-300">{t("deliverTo")}</span>
                {defaultAddress ? (
                  <span className="font-medium text-white">
                    {defaultAddress.city} {defaultAddress.postalCode}
                  </span>
                ) : (
                  <span className="font-medium text-white">
                    {t("addAddress")}
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 border rounded-full px-2 py-1 text-sm">
            <Globe className="w-4 h-4" />
            <select
              className="bg-[#242a30] cursor-pointer text-white outline-none"
              value={i18n.language}
              onChange={(e) => {
                i18n.changeLanguage(e.target.value);
                localStorage.setItem("lang", e.target.value);
              }}
            >
              <option value="en">🇺🇸 ENG</option>
              <option value="hi">🇮🇳 हिंदी</option>
              <option value="es">🇪🇸 ESP</option>
              <option value="zh">🇨🇳 中文</option>
            </select>
          </div>

          {/* SEARCH BAR */}
          <div className="flex-1 relative flex items-center mx-4">
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleSearchEnter}
              placeholder={t("search")}
              className="w-full px-4 py-3 text-black rounded-l-md focus:outline-none text-sm"
            />
            <button
              onClick={triggerSearch}
              className="bg-[#e6aa11] hover:bg-[#e1a000] px-4 py-2 rounded-r-md"
            >
              <Search className="w-7 h-7 text-[#131921]" />
            </button>

            {/* SEARCH DROPDOWN */}
            {showResults && (
              <div className="absolute top-12 left-0 w-full bg-[#131921] text-white shadow-lg border rounded-md z-[999] max-h-80 overflow-y-auto">
                {loadingSearch && (
                  <p className="px-4 py-2 text-gray-500 text-sm">Searching…</p>
                )}
                {!loadingSearch && searchResults.length === 0 && (
                  <p className="px-4 py-2 text-gray-500 text-sm">
                    No results found
                  </p>
                )}
                {searchResults.map((item) => (
                  <div
                    key={item._id}
                    onClick={() => {
                      navigate(`/product-details/${item._id}`);
                      setShowResults(false);
                    }}
                    className="px-4 py-2 cursor-pointer hover:bg-gray-100 hover:text-yellow-400 flex gap-3"
                  >
                    <img
                      src={
                        item.images?.[0] || "https://via.placeholder.com/200"
                      }
                      alt={item.name}
                      className="w-10 h-10 object-cover rounded"
                    />
                    <div>
                      <p className="font-medium text-sm">{getProductName(item)}</p>
                      <p className="text-xs text-gray-500">
                        {formatingPrice(convertPrice(item.price), currency)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ACCOUNT DROPDOWN - Shows all menu items even when not logged in */}
          <div
            className="relative"
            onMouseEnter={() => {
              clearTimeout(window.dropdownTimeout);
              setIsHovered(true);
            }}
            onMouseLeave={() => {
              window.dropdownTimeout = setTimeout(
                () => setIsHovered(false),
                150
              );
            }}
          >
            {loggedInUser ? (
              <div className="cursor-pointer px-2 py-1 hover:bg-[#232F3E] rounded-md flex flex-col">
                <span className="text-xs text-gray-300">
                  {t("hello")}, {loggedInUser.firstName || t("user")}
                </span>
                <span className="font-medium text-sm flex items-center gap-1">
                  {t("accountLists")}
                  <ChevronDown className="w-4 h-4" />
                </span>
              </div>
            ) : (
              <div
                className="cursor-pointer px-2 py-1 hover:bg-[#232F3E] rounded-md flex flex-col"
              >
                <span className="text-xs text-gray-300">
                  {t("helloSignin")}
                </span>
                <span className="font-medium text-sm flex items-center gap-1">
                  {t("accountLists")} <ChevronDown />
                </span>
              </div>
            )}

            {/* DROPDOWN - Shows all items for both logged in and guest users */}
            {isHovered && (
              <div
                className="absolute z-[999] right-0 mt-2 bg-white text-black rounded-lg shadow-xl w-44 py-2 border border-gray-200"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                {/* Profile - Always visible */}
                <div
                  onClick={() => {
                    if (loggedInUser) {
                      navigate("/Userprofile");
                    } else {
                      setShowLoginModal(true);
                    }
                  }}
                  className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                >
                  <User size={20} className="text-gray-600" />
                  <span>{loggedInUser ? t("yourProfile") : t("signIn")}</span>
                </div>

                {/* Orders - Always visible */}
                <div
                  onClick={() => {
                    if (loggedInUser) {
                      navigate("/user-orders");
                    } else {
                      setShowLoginModal(true);
                    }
                  }}
                  className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                >
                  <Box size={20} className="text-gray-600" />
                  <span>{t("yourOrders")}</span>
                </div>

                {/* Wishlist - Always visible, navigates to wishlist page */}
                <div
                  onClick={() => navigate("/wishlist")}
                  className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                >
                  <Heart size={20} className="text-gray-600" />
                  <span>{t("wishlist")}</span>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-200 my-1"></div>

                {/* Logout/Sign In - Always visible */}
                {loggedInUser ? (
                  <div
                    onClick={handleLogout}
                    className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer text-red-500"
                  >
                    <LogOut size={20} className="text-red-500" />
                    <span>{t("logout")}</span>
                  </div>
                ) : (
                  <div
                    onClick={() => setShowLoginModal(true)}
                    className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer text-blue-600"
                  >
                    <LogIn size={20} className="text-blue-600" />
                    <span>{t("signIn")}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* CURRENCY */}
          <div className="flex items-center gap-2 border rounded-full px-2 py-1 text-sm">
            <select
              className="bg-[#242a30] cursor-pointer text-white outline-none"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            >
              <option value="INR">₹ INR</option>
              <option value="USD">$ USD</option>
              <option value="EUR">€ EUR</option>
              <option value="CNY">¥ CNY</option>
            </select>
          </div>

          {/* CART */}
          <div
            onClick={handleCartClick}
            className="flex items-center gap-2 cursor-pointer hover:bg-[#232F3E] rounded-md px-3 py-2"
          >
            <div className="relative">
              <ShoppingCart className="w-6 h-6" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#febd69] text-[#131921] text-xs font-medium rounded-full w-4 h-4 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </div>
            <span className="font-normal text-sm">{t("cart")}</span>
          </div>

          <div
            onClick={() => navigate("/seller-registration")}
            className="flex items-center gap-2 cursor-pointer px-3 py-2 hover:bg-[#232F3E]"
          >
            <Store className="w-5 h-5 text-white" />
            <span className="font-normal text-sm">{t("becomeSeller")}</span>
          </div>
        </div>

        {/* MOBILE VIEW */}
        <div className="md:hidden flex items-center justify-between px-4 py-2">
          <img
            src={logo}
            alt="Logo"
            className="h-10 w-auto cursor-pointer"
            onClick={() => navigate("/")}
          />

          <div className="flex items-center gap-4 relative">
            <Search
              className="w-5 h-5 cursor-pointer"
              onClick={() => setIsSearchVisible(!isSearchVisible)}
            />
            
            {/* Mobile Cart */}
            <div
              onClick={handleCartClick}
              className="flex items-center gap-2 cursor-pointer relative"
            >
              <div className="relative">
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#febd69] text-[#131921] text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </div>
            </div>
            
            <div
              onClick={() => navigate("/seller-registration")}
              className="flex items-center gap-1 cursor-pointer"
            >
              <Store className="w-5 h-5" />
            </div>

            {/* User dropdown - Mobile */}
            <div className="relative" ref={dropdownRef}>
              <User
                className="w-5 h-5 cursor-pointer"
                onClick={() => setShowUserDropdown(!showUserDropdown)}
              />
              {showUserDropdown && (
                <div className="absolute right-0 mt-2 bg-white text-black rounded-md shadow-lg w-40 py-2 border">
                  {/* Profile */}
                  <div
                    onClick={() => {
                      if (loggedInUser) {
                        navigate("/Userprofile");
                        setShowUserDropdown(false);
                      } else {
                        setShowUserDropdown(false);
                        setShowLoginModal(true);
                      }
                    }}
                    className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                  >
                    <User size={20} className="text-gray-600" />
                    <span>{loggedInUser ? t("yourProfile") : t("signIn")}</span>
                  </div>

                  {/* Orders */}
                  <div
                    onClick={() => {
                      if (loggedInUser) {
                        navigate("/user-orders");
                        setShowUserDropdown(false);
                      } else {
                        setShowUserDropdown(false);
                        setShowLoginModal(true);
                      }
                    }}
                    className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                  >
                    <Box size={20} className="text-gray-600" />
                    <span>{t("yourOrders")}</span>
                  </div>

                  {/* Wishlist - Always navigates to wishlist */}
                  <div
                    onClick={() => {
                      navigate("/wishlist");
                      setShowUserDropdown(false);
                    }}
                    className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                  >
                    <Heart size={20} className="text-gray-600" />
                    <span>{t("wishlist")}</span>
                  </div>

                  {/* Divider */}
                  <div className="border-t border-gray-200 my-1"></div>

                  {/* Logout/Sign In */}
                  {loggedInUser ? (
                    <div
                      onClick={() => {
                        handleLogout();
                        setShowUserDropdown(false);
                      }}
                      className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer text-red-500"
                    >
                      <LogOut size={20} className="text-red-500" />
                      <span>{t("logout")}</span>
                    </div>
                  ) : (
                    <div
                      onClick={() => {
                        setShowUserDropdown(false);
                        setShowLoginModal(true);
                      }}
                      className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer text-blue-600"
                    >
                      <LogIn size={20} className="text-blue-600" />
                      <span>{t("signIn")}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* MOBILE SEARCH */}
        {isSearchVisible && (
          <div className="md:hidden relative bg-[#232F3E] px-3 py-2">
            <div className="flex items-center">
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleSearchEnter}
                placeholder={t("search")}
                className="flex-1 px-3 py-2 text-black rounded-l-md text-sm"
              />
              <button
                onClick={() => {
                  triggerSearch();
                  setIsSearchVisible(false);
                  setShowResults(false);
                }}
                className="bg-[#febd69] px-4 py-2 rounded-r-md"
              >
                <Search className="w-5 h-5 text-[#131921]" />
              </button>
            </div>

            {showResults && (
              <div className="absolute top-14 left-0 w-full bg-[#131921] text-white shadow-lg border rounded-md z-[999] max-h-80 overflow-y-auto">
                {loadingSearch && (
                  <p className="px-4 py-2 text-gray-500 text-sm">Searching…</p>
                )}
                {!loadingSearch && searchResults.length === 0 && (
                  <p className="px-4 py-2 text-gray-500 text-sm">
                    No results found
                  </p>
                )}
                {searchResults.map((item) => (
                  <div
                    key={item._id}
                    onClick={() => {
                      navigate(`/product-details/${item._id}`);
                      setShowResults(false);
                      setIsSearchVisible(false);
                    }}
                    className="px-4 py-2 cursor-pointer hover:bg-gray-100 hover:text-yellow-400 flex gap-3"
                  >
                    <img
                      src={
                        item.images?.[0] || "https://via.placeholder.com/200"
                      }
                      alt={item.name}
                      className="w-10 h-10 object-cover rounded"
                    />
                    <div>
                      <p className="font-medium text-sm">{getProductName(item)}</p>
                      <p className="text-xs text-gray-500">
                        {formatingPrice(convertPrice(item.price), currency)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </nav>
    </>
  );
};

export default TopNavbar;


// import React, { useState, useEffect, useRef } from "react";
// import {
//   MapPin,
//   ShoppingCart,
//   User,
//   Search,
//   ChevronDown,
//   Globe,
//   Store,
//   Heart,
//   Box,
//   LogOut,
//   LogIn,
// } from "lucide-react";
// import logo from "../../Assets/Logos/LogoKarigari.png";
// import { useNavigate } from "react-router-dom";
// import UserLoginModal from "../Login/UserLoginModal";
// import toast from "react-hot-toast";
// import { useCart } from "../../utils/CartContext";
// import AddAddressModal from "../UserSideComponents/Address/AddAddressModal";
// import AllAddressesModal from "../UserSideComponents/Address/AllAddressesModal";
// import { useTranslation } from "react-i18next";
// import { useCurrency } from "../../context/CurrencyContext";
// import { formatingPrice } from "../../utils/formatingPrice";

// const TopNavbar = () => {
//   const { t, i18n } = useTranslation();
//   const { currency, setCurrency, convertPrice } = useCurrency();

//   const [searchQuery, setSearchQuery] = useState("");
//   const [showLoginModal, setShowLoginModal] = useState(false);
//   const [loggedInUser, setLoggedInUser] = useState(null);
//   const [isHovered, setIsHovered] = useState(false);
//   const [isSearchVisible, setIsSearchVisible] = useState(false);
//   const [showUserDropdown, setShowUserDropdown] = useState(false);
//   const [defaultAddress, setDefaultAddress] = useState(null);
//   const [allAddresses, setAllAddresses] = useState([]);
//   const dropdownRef = useRef(null);
//   const navigate = useNavigate();
//   const { cartCount, fetchCartCount } = useCart();
//   const [showAddAddressModal, setShowAddAddressModal] = useState(false);
//   const [showAllAddressesModal, setShowAllAddressesModal] = useState(false);
//   const [allProducts, setAllProducts] = useState([]);
//   const [searchResults, setSearchResults] = useState([]);
//   const [showResults, setShowResults] = useState(false);
//   const [loadingSearch, setLoadingSearch] = useState(false);
//   const [products, setProducts] = useState([]);
//   const [addressesFetched, setAddressesFetched] = useState(false);

//   useEffect(() => {
//     const stored = localStorage.getItem("pm-user-data");
//     if (stored) {
//       const user = JSON.parse(stored);
//       setLoggedInUser(user);
//       fetchCartCount(user._id);
//       fetchUserAddresses(user._id);
//     }
//   }, [fetchCartCount]);

//   // Add this useEffect in TopNavbar.js to listen for login events
// useEffect(() => {
//   const handleStorageChange = (e) => {
//     if (e.key === 'pm-user-data') {
//       const stored = localStorage.getItem("pm-user-data");
//       if (stored) {
//         const user = JSON.parse(stored);
//         setLoggedInUser(user);
//         fetchCartCount(user._id);
//         fetchUserAddresses(user._id);
//       } else {
//         setLoggedInUser(null);
//         setDefaultAddress(null);
//         setAllAddresses([]);
//         setAddressesFetched(false);
//       }
//     }
//   };

//   window.addEventListener('storage', handleStorageChange);
//   return () => window.removeEventListener('storage', handleStorageChange);
// }, []);

//   const fetchUserAddresses = async (userId) => {
//     try {
//       const res = await fetch(
//         `${process.env.REACT_APP_API_URL}/address/user/${userId}`
//       );
//       const data = await res.json();
//       if (data.success && data.data.length > 0) {
//         setAllAddresses(data.data);
//         const defaultAddr = data.data.find((a) => a.isDefault);
//         if (defaultAddr) {
//           setDefaultAddress(defaultAddr);
//         } else {
//           setDefaultAddress(data.data[0]);
//         }
//       }
//       setAddressesFetched(true);
//     } catch (err) {
//       console.error("Address fetch error:", err);
//       setAddressesFetched(true);
//     }
//   };

//   const handleLoginSuccess = (userData) => {
//     setLoggedInUser(userData);
//     localStorage.setItem("pm-user-data", JSON.stringify(userData));
//     setShowLoginModal(false);
//     toast.success(`${t("hello")}, ${userData.firstName || t("user")}!`);
//     fetchCartCount(userData._id);
//     fetchUserAddresses(userData._id);
//   };

//   const handleLogout = () => {
//     localStorage.removeItem("pm-user-data");
//     setLoggedInUser(null);
//     setDefaultAddress(null);
//     setAllAddresses([]);
//     setAddressesFetched(false);
//     toast.success(t("logoutSuccess"));
//     fetchCartCount(null);
//   };

//   const handleAddressSelect = (address) => {
//     setDefaultAddress(address);
//     setShowAllAddressesModal(false);
//   };

//   const handleAddAddressClick = () => {
//     if (allAddresses.length > 0) {
//       setShowAllAddressesModal(true);
//     } else {
//       setShowAddAddressModal(true);
//     }
//   };

//   const handleAddressAdded = (newAddress) => {
//     setDefaultAddress(newAddress);
//     setShowAddAddressModal(false);
//     setAllAddresses([...allAddresses, newAddress]);
//   };

//   // Handle cart click - navigates to cart page for both logged in and guest users
//   const handleCartClick = () => {
//     navigate("/cart");
//   };

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setShowUserDropdown(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   useEffect(() => {
//     const fetchAll = async () => {
//       try {
//         const res = await fetch(
//           `${process.env.REACT_APP_API_URL}/products/get-all-products`
//         );
//         const data = await res.json();
//         setAllProducts(data.products || []);
//         setProducts(data.products || []);
//       } catch (err) {
//         console.error("Error fetching all products:", err);
//       }
//     };
//     fetchAll();
//   }, []);

//   const handleSearchChange = (e) => {
//     const value = e.target.value;
//     setSearchQuery(value);

//     if (value.trim() === "") {
//       setShowResults(false);
//       setSearchResults([]);
//       return;
//     }

//     const results = filterProducts(value, products);
//     setSearchResults(results);
//     setShowResults(true);
//   };

//   useEffect(() => {
//     if (searchQuery.trim().length > 0) {
//       setShowResults(true);
//     }
//   }, [searchQuery]);

//   const getProductName = (product) => {
//     const name = product?.name;
//     if (!name) return "";
//     // Handle multilingual object
//     if (typeof name === "object") {
//       return (
//         name[i18n.language] ||
//         name.en ||
//         name.hi ||
//         name.es ||
//         name.zh ||
//         ""
//       );
//     }
//     // Plain string (backward compat)
//     return name || product.title || product.headerTitle || product.productName || "";
//   };

//   const filterProducts = (query, products) => {
//     if (!query.trim()) return [];
//     const lower = query.toLowerCase();
//     return products.filter((p) => {
//       const name = getProductName(p);
//       if (!name || typeof name !== "string") return false;
//       return name.toLowerCase().includes(lower);
//     });
//   };

//   const handleSearchEnter = (e) => {
//     if (e.key === "Enter") triggerSearch();
//   };

//   const triggerSearch = () => {
//     if (!searchQuery.trim()) return;
//     navigate(`/allproduct?search=${encodeURIComponent(searchQuery)}`);
//     setShowResults(false);
//   };

//   return (
//     <>
//       {showLoginModal && (
//         <UserLoginModal
//           onClose={() => setShowLoginModal(false)}
//           onLoginSuccess={handleLoginSuccess}
//         />
//       )}

//       {showAddAddressModal && (
//         <AddAddressModal
//           isOpen={showAddAddressModal}
//           onClose={() => setShowAddAddressModal(false)}
//           onAddressAdded={handleAddressAdded}
//         />
//       )}

//       {showAllAddressesModal && (
//         <AllAddressesModal
//           isOpen={showAllAddressesModal}
//           onClose={() => setShowAllAddressesModal(false)}
//           addresses={allAddresses}
//           onAddressSelect={handleAddressSelect}
//           selectedAddressId={defaultAddress?._id}
//           onAddAddressClick={() => setShowAddAddressModal(true)}
//         />
//       )}

//       {/*  NAVBAR */}
//       <nav className="bg-[#242a30] text-white sticky top-0 z-[999]">
//         {/* DESKTOP VIEW */}
//         <div className="hidden md:flex items-center justify-between px-4 md:px-10 py-2 gap-4">
//           {/* LOGO */}
//           <div
//             onClick={() => navigate("/")}
//             className="flex items-center gap-2 cursor-pointer"
//           >
//             <img src={logo} alt="Logo" className="h-12 w-auto" />
//           </div>

//           {loggedInUser && (
//             <div
//               className="flex items-center gap-2 cursor-pointer hover:bg-[#232F3E] p-2 rounded-md"
//               onClick={handleAddAddressClick}
//             >
//               <MapPin className="w-5 h-5 text-white" />

//               <div className="text-xs flex flex-col">
//                 <span className="text-gray-300">{t("deliverTo")}</span>

//                 {defaultAddress ? (
//                   <span className="font-medium text-white">
//                     {defaultAddress.city} {defaultAddress.postalCode}
//                   </span>
//                 ) : (
//                   <span className="font-medium text-white">
//                     {t("addAddress")}
//                   </span>
//                 )}
//               </div>
//             </div>
//           )}

//           <div className="flex items-center gap-2 border rounded-full px-2 py-1 text-sm">
//             <Globe className="w-4 h-4" />
//             <select
//               className="bg-[#242a30] cursor-pointer text-white outline-none"
//               value={i18n.language}
//               onChange={(e) => {
//                 i18n.changeLanguage(e.target.value);
//                 localStorage.setItem("lang", e.target.value);
//               }}
//             >
//               <option value="en">🇺🇸 ENG</option>
//               <option value="hi">🇮🇳 हिंदी</option>
//               <option value="es">🇪🇸 ESP</option>
//               <option value="zh">🇨🇳 中文</option>
//             </select>
//           </div>

//           {/* SEARCH BAR */}
//           <div className="flex-1 relative flex items-center mx-4">
//             <input
//               type="text"
//               value={searchQuery}
//               onChange={handleSearchChange}
//               onKeyDown={handleSearchEnter}
//               placeholder={t("search")}
//               className="w-full px-4 py-3 text-black rounded-l-md focus:outline-none text-sm"
//             />
//             <button
//               onClick={triggerSearch}
//               className="bg-[#e6aa11] hover:bg-[#e1a000] px-4 py-2 rounded-r-md"
//             >
//               <Search className="w-7 h-7 text-[#131921]" />
//             </button>

//             {/* SEARCH DROPDOWN */}
//             {showResults && (
//               <div className="absolute top-12 left-0 w-full bg-[#131921] text-white shadow-lg border rounded-md z-[999] max-h-80 overflow-y-auto">
//                 {loadingSearch && (
//                   <p className="px-4 py-2 text-gray-500 text-sm">Searching…</p>
//                 )}

//                 {!loadingSearch && searchResults.length === 0 && (
//                   <p className="px-4 py-2 text-gray-500 text-sm">
//                     No results found
//                   </p>
//                 )}

//                 {searchResults.map((item) => (
//                   <div
//                     key={item._id}
//                     onClick={() => {
//                       navigate(`/product-details/${item._id}`);
//                       setShowResults(false);
//                     }}
//                     className="px-4 py-2 cursor-pointer hover:bg-gray-100 hover:text-yellow-400 flex gap-3"
//                   >
//                     <img
//                       src={
//                         item.images?.[0] || "https://via.placeholder.com/200"
//                       }
//                       alt={item.name}
//                       className="w-10 h-10 object-cover rounded"
//                     />
//                     <div>
//                       <p className="font-medium text-sm">{getProductName(item)}</p>
//                       <p className="text-xs text-gray-500">
//                         {formatingPrice(convertPrice(item.price), currency)}
//                       </p>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             )}
//           </div>

//           {/* ACCOUNT DROPDOWN (DESKTOP) - Only this should open login modal */}
//           <div
//             className="relative"
//             onMouseEnter={() => {
//               clearTimeout(window.dropdownTimeout);
//               setIsHovered(true);
//             }}
//             onMouseLeave={() => {
//               window.dropdownTimeout = setTimeout(
//                 () => setIsHovered(false),
//                 150
//               );
//             }}
//           >
//             {loggedInUser ? (
//               <div className="cursor-pointer px-2 py-1 hover:bg-[#232F3E] rounded-md flex flex-col">
//                 <span className="text-xs text-gray-300">
//                   {t("hello")}, {loggedInUser.firstName || t("user")}
//                 </span>
//                 <span className="font-medium text-sm flex items-center gap-1">
//                   {t("accountLists")}
//                   <ChevronDown className="w-4 h-4" />
//                 </span>
//               </div>
//             ) : (
//               <div
//                 onClick={() => setShowLoginModal(true)}
//                 className="cursor-pointer px-2 py-1 hover:bg-[#232F3E] rounded-md flex flex-col"
//               >
//                 <span className="text-xs text-gray-300">
//                   {t("helloSignin")}
//                 </span>
//                 <span className="font-medium text-sm flex items-center gap-1">
//                   {t("accountLists")} <ChevronDown />
//                 </span>
//               </div>
//             )}

//             {/* DROPDOWN */}
//             {isHovered && (
//               <div
//                 className="absolute z-[999] right-0 mt-2 bg-white text-black rounded-lg shadow-xl w-44 py-2 border border-gray-200"
//                 onMouseEnter={() => setIsHovered(true)}
//                 onMouseLeave={() => setIsHovered(false)}
//               >
//                 {loggedInUser ? (
//                   <>
//                     <div
//                       onClick={() => navigate("/Userprofile")}
//                       className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
//                     >
//                       <User size={20} className="text-gray-600" />
//                       <span>{t("yourProfile")}</span>
//                     </div>

//                     <div
//                       onClick={() => navigate("/user-orders")}
//                       className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
//                     >
//                       <Box size={20} className="text-gray-600" />
//                       <span>{t("yourOrders")}</span>
//                     </div>

//                     <div
//                       onClick={() => navigate("/wishlist")}
//                       className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
//                     >
//                       <Heart size={20} className="text-gray-600" />
//                       <span>{t("wishlist")}</span>
//                     </div>

//                     <div
//                       onClick={handleLogout}
//                       className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer text-red-500"
//                     >
//                       <LogOut size={20} className="text-red-500" />
//                       <span>{t("logout")}</span>
//                     </div>
//                   </>
//                 ) : (
//                   <div
//                     onClick={() => setShowLoginModal(true)}
//                     className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
//                   >
//                     <LogIn size={20} className="text-gray-600" />
//                     <span>{t("signIn")}</span>
//                   </div>
//                 )}
//               </div>
//             )}
//           </div>

//           {/* CURRENCY */}
//           <div className="flex items-center gap-2 border rounded-full px-2 py-1 text-sm">
//             <select
//               className="bg-[#242a30] cursor-pointer text-white outline-none"
//               value={currency}
//               onChange={(e) => setCurrency(e.target.value)}
//             >
//               <option value="INR">₹ INR</option>
//               <option value="USD">$ USD</option>
//               <option value="EUR">€ EUR</option>
//               <option value="CNY">¥ CNY</option>
//             </select>
//           </div>

//           {/* CART - Always navigates to cart page for both logged in and guest users */}
//           <div
//             onClick={handleCartClick}
//             className="flex items-center gap-2 cursor-pointer hover:bg-[#232F3E] rounded-md px-3 py-2"
//           >
//             <div className="relative">
//               <ShoppingCart className="w-6 h-6" />
//               {cartCount > 0 && (
//                 <span className="absolute -top-1 -right-1 bg-[#febd69] text-[#131921] text-xs font-medium rounded-full w-4 h-4 flex items-center justify-center">
//                   {cartCount}
//                 </span>
//               )}
//             </div>
//             <span className="font-normal text-sm">{t("cart")}</span>
//           </div>

//           <div
//             onClick={() => navigate("/seller-registration")}
//             className="flex items-center gap-2 cursor-pointer px-3 py-2 hover:bg-[#232F3E]"
//           >
//             <Store className="w-5 h-5 text-white" />
//             <span className="font-normal text-sm">{t("becomeSeller")}</span>
//           </div>
//         </div>

//         {/* MOBILE VIEW */}
//         <div className="md:hidden flex items-center justify-between px-4 py-2">
//           <img
//             src={logo}
//             alt="Logo"
//             className="h-10 w-auto cursor-pointer"
//             onClick={() => navigate("/")}
//           />

//           <div className="flex items-center gap-4 relative">
//             <Search
//               className="w-5 h-5 cursor-pointer"
//               onClick={() => setIsSearchVisible(!isSearchVisible)}
//             />
            
//             {/* Mobile Cart - Always navigates to cart page */}
//             <div
//               onClick={handleCartClick}
//               className="flex items-center gap-2 cursor-pointer relative"
//             >
//               <div className="relative">
//                 <ShoppingCart className="w-5 h-5" />
//                 {cartCount > 0 && (
//                   <span className="absolute -top-2 -right-2 bg-[#febd69] text-[#131921] text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
//                     {cartCount}
//                   </span>
//                 )}
//               </div>
//             </div>
            
//             <div
//               onClick={() => navigate("/seller-registration")}
//               className="flex items-center gap-1 cursor-pointer"
//             >
//               <Store className="w-5 h-5" />
//             </div>

//             {/* User dropdown - Only this should open login modal */}
//             <div className="relative" ref={dropdownRef}>
//               <User
//                 className="w-5 h-5 cursor-pointer"
//                 onClick={() => setShowUserDropdown(!showUserDropdown)}
//               />
//               {showUserDropdown && (
//                 <div className="absolute right-0 mt-2 bg-white text-black rounded-md shadow-lg w-40 py-2 border">
//                   {loggedInUser ? (
//                     <>
//                       <div
//                         onClick={() => navigate("/Userprofile")}
//                         className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
//                       >
//                         <User size={20} className="text-gray-600" />
//                         <span>{t("yourProfile")}</span>
//                       </div>

//                       <div
//                         onClick={() => navigate("/user-orders")}
//                         className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
//                       >
//                         <Box size={20} className="text-gray-600" />
//                         <span>{t("yourOrders")}</span>
//                       </div>

//                       <div
//                         onClick={() => navigate("/wishlist")}
//                         className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
//                       >
//                         <Heart size={20} className="text-gray-600" />
//                         <span>{t("wishlist")}</span>
//                       </div>

//                       <div
//                         onClick={handleLogout}
//                         className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer text-red-500"
//                       >
//                         <LogOut size={20} className="text-red-500" />
//                         <span>{t("logout")}</span>
//                       </div>
//                     </>
//                   ) : (
//                     <div
//                       onClick={() => {
//                         setShowUserDropdown(false);
//                         setShowLoginModal(true);
//                       }}
//                       className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
//                     >
//                       <LogIn size={20} className="text-gray-600" />
//                       <span>Sign In</span>
//                     </div>
//                   )}
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>

//         {/* MOBILE SEARCH */}
//         {isSearchVisible && (
//           <div className="md:hidden relative bg-[#232F3E] px-3 py-2">
//             <div className="flex items-center">
//               <input
//                 type="text"
//                 value={searchQuery}
//                 onChange={handleSearchChange}
//                 onKeyDown={handleSearchEnter}
//                 placeholder={t("search")}
//                 className="flex-1 px-3 py-2 text-black rounded-l-md text-sm"
//               />
//               <button
//                 onClick={() => {
//                   triggerSearch();
//                   setIsSearchVisible(false);
//                   setShowResults(false);
//                 }}
//                 className="bg-[#febd69] px-4 py-2 rounded-r-md"
//               >
//                 <Search className="w-5 h-5 text-[#131921]" />
//               </button>
//             </div>

//             {showResults && (
//               <div className="absolute top-14 left-0 w-full bg-[#131921] text-white shadow-lg border rounded-md z-[999] max-h-80 overflow-y-auto">
//                 {loadingSearch && (
//                   <p className="px-4 py-2 text-gray-500 text-sm">Searching…</p>
//                 )}

//                 {!loadingSearch && searchResults.length === 0 && (
//                   <p className="px-4 py-2 text-gray-500 text-sm">
//                     No results found
//                   </p>
//                 )}

//                 {searchResults.map((item) => (
//                   <div
//                     key={item._id}
//                     onClick={() => {
//                       navigate(`/product-details/${item._id}`);
//                       setShowResults(false);
//                       setIsSearchVisible(false);
//                     }}
//                     className="px-4 py-2 cursor-pointer hover:bg-gray-100 hover:text-yellow-400 flex gap-3"
//                   >
//                     <img
//                       src={
//                         item.images?.[0] || "https://via.placeholder.com/200"
//                       }
//                       alt={item.name}
//                       className="w-10 h-10 object-cover rounded"
//                     />
//                     <div>
//                       <p className="font-medium text-sm">{getProductName(item)}</p>
//                       <p className="text-xs text-gray-500">
//                         {formatingPrice(convertPrice(item.price), currency)}
//                       </p>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             )}
//           </div>
//         )}
//       </nav>
//     </>
//   );
// };

// export default TopNavbar;